import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from './useAuth';
import { registerIfGranted, setPushNavigate, primeBusinessPushOnLogin } from '../utils/pushClient';
import { isBusinessApp } from '../utils/platform';

// Keeps this device's APNs token fresh while signed in — WITHOUT prompting.
//
// Per Apple's guidance we no longer ask for notification permission cold at
// login. This hook only does the SILENT half: if the user has already granted
// permission, re-register and re-POST the token (Apple: fetch a fresh token each
// launch), and wire the tap handler for deep-linking. The actual permission
// REQUEST happens in context after the first booking (PushPrimingSheet, fired
// via triggerPushPrompt()). All native/iOS gating lives in pushClient.
//
// Mounted via a null <PushRegistrar/> inside AuthProvider so `user` and the
// router exist.
export default function usePushNotifications() {
  const { user } = useAuth();
  const userId = user?.id;
  const role = user?.role;
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;
    setPushNavigate(navigate);
    registerIfGranted();
    // Business app: ask for permission immediately after login (the owner wants
    // alerts) — but ONLY for staff roles. A CLIENT here is mid-salon-
    // registration (the two-step /pro/register flow): firing the one-shot OS
    // permission dialog over "name your salon" burns it at the worst moment.
    // The effect re-runs when the role flips to SALON_OWNER, priming then.
    // Client app: no-op — permission is primed after the first booking.
    if (isBusinessApp && role && role !== 'CLIENT') primeBusinessPushOnLogin();
    // Key on user ID + role (a NEW object with the same id must not re-run,
    // but the CLIENT→SALON_OWNER flip must).
  }, [userId, role, navigate]);
}
